import {
  GitCommitIcon,
  InfoCircleIcon,
  PlusIcon,
  Tag01Icon,
  Trash04Icon,
} from '@langchain/untitled-ui-icons';
import { Button, Checkbox, Input } from '@mui/joy';

import { useEffect, useState } from 'react';

import { DocsButton } from '@/Pages/SingleDashboard/components/DocsButton';
import { DeleteConfirmationButton } from '@/components/Delete';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import useToast from '@/components/Toast/useToast';
import { Tooltip } from '@/design-system';
import {
  useCreateRepoCommitTag,
  useDeleteRepoCommitTag,
  useRepoCommitTags,
  useUpdateRepoCommitTag,
} from '@/hooks/useSwr';
import { CommitWithLookupsSchema, RepoCommitTag } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { deleteTagDisclaimer } from '../constants';

const NewTagDropdownButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      type="button"
      className={cn(
        'w-full cursor-pointer justify-center rounded-md border border-secondary py-1.5 hover:bg-secondary-hover'
      )}
      onClick={onClick}
    >
      <span className="flex flex-row items-center justify-center gap-2 text-sm font-medium text-primary">
        <PlusIcon className="h-4 w-4" />
        Commit Tag
      </span>
    </button>
  );
};

const CommitHashTag = ({
  hash,
  selected,
}: {
  hash: string;
  selected: boolean;
}) => {
  return (
    <div
      className={cn(
        'flex flex-row gap-1 rounded-md px-0.5',
        selected
          ? 'bg-brand-green-50 text-brand-green-700'
          : 'bg-tertiary text-secondary'
      )}
    >
      <GitCommitIcon className={cn('my-auto h-4 w-4 font-medium')} />
      <span className={cn('font-medium')}>{hash}</span>
    </div>
  );
};

const TagButtonDropdownItem = ({
  tag,
  commit,
  owner,
  repoName,
}: {
  tag: RepoCommitTag;
  commit: CommitWithLookupsSchema;
  owner?: string;
  repoName?: string;
}) => {
  const { createToast } = useToast();
  const selected = tag.commit_id === commit.id;
  const { trigger: triggerUpdate } = useUpdateRepoCommitTag(
    owner,
    repoName,
    tag.tag_name,
    { commit_id: commit.id },
    {
      onError: (e) =>
        createToast({
          title: 'Failed to update tag',
          error: true,
          description: e.message || 'There was an error, try again',
        }),
    }
  );
  const { trigger: triggerDelete, isMutating: isDeleteLoading } =
    useDeleteRepoCommitTag(owner, repoName, tag.tag_name, {
      onError: (e) =>
        createToast({
          title: 'Failed to delete tag',
          error: true,
          description: e.message || 'There was an error, try again',
        }),
    });

  return (
    <div
      className={cn(
        'group relative flex w-full flex-row items-center gap-1 px-2 py-1.5',
        !selected && 'cursor-pointer hover:bg-secondary-hover '
      )}
      onClick={() => (!selected ? triggerUpdate({}) : undefined)}
    >
      <div className="my-auto mr-2 flex w-full flex-row gap-2 text-sm text-primary">
        <Checkbox checked={selected} size="sm" className="my-auto" />
        <span className="max-w-[110px] truncate">{tag.tag_name}</span>
        <CommitHashTag hash={tag.commit_hash.slice(0, 8)} selected={selected} />
      </div>
      <div>
        <Tooltip
          title={deleteTagDisclaimer}
          side="top"
          tooltipClassName="max-w-[300px]"
        >
          <div className="flex items-center">
            <DeleteConfirmationButton
              onDelete={async () => {
                await triggerDelete({});
              }}
              title="Confirm delete tag"
              description={
                <span>Are you sure you want to delete this tag?</span>
              }
              deleteButtonText="Delete"
              trigger={
                <div className="cursor-pointer p-1 hover:opacity-50">
                  <Trash04Icon className="h-4 w-4" />
                </div>
              }
              isDeleting={isDeleteLoading}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

const NewTagPopoverContent = ({
  isOpen,
  onSave,
  isLoading,
  tagName,
  setTagName,
  onClose,
}: {
  isOpen: boolean;
  onSave: (tagName: string) => void;
  isLoading: boolean;
  tagName: string;
  setTagName: (tagName: string) => void;
  onClose: () => void;
}) => {
  const [error, setError] = useState<string | null>(null);

  const validateTagName = (name: string) => {
    if (!name) return null;
    if (name === 'latest') return 'Tag name cannot be "latest"';

    return /^[a-zA-Z0-9_-]+$/.test(name)
      ? null
      : 'Tag name can only contain letters, numbers, hyphens, and underscores';
  };

  useEffect(() => {
    if (!isOpen) {
      setTagName('');
      setError(null);
    } else if (tagName) {
      setError(validateTagName(tagName));
    }
  }, [isOpen, tagName, setTagName]);

  return (
    <div>
      <div className="mb-2 flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2">
          <label className="text-xs font-medium uppercase text-tertiary">
            Commit tag name
          </label>
          <Tooltip
            title="Commit tag name must only contain letters, numbers, hyphens and underscores"
            side="top"
          >
            <div>
              <InfoCircleIcon className="h-4 w-4 text-tertiary" />
            </div>
          </Tooltip>
        </div>
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            value={tagName}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !error) {
                onSave(tagName);
              }
            }}
            onChange={(e) => setTagName(e.target.value)}
            placeholder="ex. development, staging, etc."
            className={`h-10 w-full rounded-md border px-3 py-2 ${
              error ? 'border-red-500' : 'border-tertiary'
            }`}
          />
          <p className={cn('mt-1 text-xs text-red-500', !error && 'invisible')}>
            {error}
          </p>
        </div>
      </div>
      <div className="flex flex-row justify-end gap-2">
        <Button variant="outlined" onClick={onClose} size="sm">
          Cancel
        </Button>
        <Button
          variant="solid"
          size="sm"
          onClick={() => onSave(tagName)}
          disabled={!!error || !tagName}
          loading={isLoading}
        >
          Create tag
        </Button>
      </div>
    </div>
  );
};

const AddCommitTagButton = ({
  commit,
  owner,
  repo,
}: {
  commit: CommitWithLookupsSchema;
  owner?: string;
  repo?: string;
}) => {
  const [isNewTagModalOpen, setIsNewTagModalOpen] = useState(false);
  const { createToast } = useToast();
  const [tagName, setTagName] = useState('');
  const { trigger: triggerCreateTag, isMutating } = useCreateRepoCommitTag(
    owner,
    repo,
    { commit_id: commit.id, tag_name: tagName },
    {
      onError: (e) =>
        createToast({
          title: 'Failed to create tag',
          error: true,
          description: e.message || 'There was an error, try again',
        }),
    }
  );

  const { data: allTags } = useRepoCommitTags(owner, repo);

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Popover>
        <PopoverTrigger asChild>
          <div>
            <Tooltip
              title={
                <div className="flex flex-row items-center gap-2">
                  <p className="font-medium">Add a Commit Tag</p>
                  <div onClick={(e) => e.stopPropagation()}>
                    <DocsButton link="https://docs.langchain.com/langsmith/manage-prompts#commit-tags" />
                  </div>
                </div>
              }
              delayDuration={100}
            >
              <button
                type="button"
                className="rounded-lg border border-primary bg-background p-1 hover:opacity-70"
              >
                <Tag01Icon className="my-auto h-4 w-4 text-quaternary" />
              </button>
            </Tooltip>
          </div>
        </PopoverTrigger>
        <PopoverContent className="m-0 w-[300px] px-0 pb-0 pt-1">
          <div className="flex w-full text-xs uppercase text-tertiary">
            <span className="mx-auto">{commit.commit_hash.slice(0, 8)}</span>
          </div>
          <div className="max-h-[250px] overflow-y-auto overflow-x-hidden">
            {allTags?.map((tag) => {
              return (
                <TagButtonDropdownItem
                  key={tag.id}
                  tag={tag}
                  commit={commit}
                  owner={owner}
                  repoName={repo}
                />
              );
            })}
          </div>
          <Popover open={isNewTagModalOpen} onOpenChange={setIsNewTagModalOpen}>
            <PopoverTrigger asChild>
              <div className="m-2">
                <NewTagDropdownButton
                  onClick={() => setIsNewTagModalOpen(true)}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent side="right" align="start">
              <NewTagPopoverContent
                isOpen={isNewTagModalOpen}
                onSave={async () => {
                  await triggerCreateTag({});
                  setIsNewTagModalOpen(false);
                }}
                isLoading={isMutating}
                tagName={tagName}
                setTagName={setTagName}
                onClose={() => {
                  setIsNewTagModalOpen(false);
                  setTagName('');
                }}
              />
            </PopoverContent>
          </Popover>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AddCommitTagButton;
